<template>
  <div class="tw-w-full">
    <Transition name="summary-expand">
      <div v-if="!hideCostSummary && expanded && hasSelections" class="tw-py-4 tw-border-b tw-border-border">
        <p class="tw-text-sm tw-font-medium tw-text-foreground tw-mb-2">{{ $t("Benefits Summary") }}</p>
        <div class="tw-text-xs tw-space-y-1">
          <div
            v-for="item in activeFlowElectionsCostSummary.selections"
            :key="item.label"
            class="tw-flex tw-flex-row tw-justify-between tw-text-muted-foreground"
          >
            <p>{{ $t(item.label) }}</p>
            <p v-if="item.waived">{{ $t("Waived") }}</p>
            <p v-else>{{ `$${item.cost}/mo` }}</p>
          </div>
        </div>
      </div>
    </Transition>

    <div class="tw-flex tw-flex-row tw-items-center tw-justify-end tw-gap-3 sm:tw-gap-6 tw-py-3 sm:tw-py-6">
      <div
        v-if="!hideCostSummary"
        class="tw-flex tw-flex-1 tw-flex-row tw-items-center tw-gap-3 sm:tw-gap-4 tw-min-w-0"
      >
        <span
          class="tw-flex tw-h-12 tw-w-12 sm:tw-h-16 sm:tw-w-16 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-full tw-bg-warning-soft tw-text-secondary"
          aria-hidden="true"
        >
          <Icon name="cost-illustration" :size="32" class="sm:tw-hidden" />
          <Icon name="cost-illustration" :size="40" class="tw-hidden sm:tw-block" />
        </span>
        <div class="tw-flex tw-flex-col tw-min-w-0">
          <span class="tw-text-sm tw-font-normal tw-text-muted-foreground">
            {{ $t("Estimated Monthly Cost") }}
          </span>
          <span class="tw-text-lg tw-font-medium tw-text-primary">
            {{ `$${activeFlowElectionsCostSummary.totalCost}/mo` }}
          </span>
        </div>
      </div>

      <button
        v-if="!hideCostSummary && hasSelections"
        v-track-click
        type="button"
        :tracking-label="'enrollment-summary-toggle-details'"
        class="tw-inline-flex tw-shrink-0 tw-items-center tw-gap-2 tw-bg-transparent tw-p-0 tw-text-sm tw-font-medium tw-text-foreground hover:tw-text-primary"
        :aria-expanded="expanded"
        :aria-label="expanded ? $t('Hide details') : $t('Show details')"
        @click="expanded = !expanded"
      >
        <span>{{ expanded ? $t("Hide details") : $t("Show details") }}</span>
        <ChevronUp v-if="expanded" class="tw-h-4 tw-w-4" />
        <ChevronDown v-else class="tw-h-4 tw-w-4" />
      </button>

      <!-- Per-page actions are teleported here from EnrollmentPage. Always rendered so
           pages can teleport their Continue/etc. buttons even when the cost summary is
           hidden (e.g. on the welcome step). -->
      <div id="enrollment-flow-footer-actions" class="tw-flex tw-shrink-0 tw-flex-row tw-items-center tw-gap-2" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from "vue"
import { storeToRefs } from "pinia"
import { ChevronDown, ChevronUp } from "lucide-vue-next"
import { useEnrollmentStore } from "@/modules/enrollment-new/stores/enrollment-new-store"
import Icon from "@/components/Icon.vue"

withDefaults(defineProps<{ hideCostSummary?: boolean }>(), { hideCostSummary: false })

const { activeFlowElectionsCostSummary } = storeToRefs(useEnrollmentStore())

const expanded = ref<boolean>(false)
const hasSelections = computed<boolean>(() => activeFlowElectionsCostSummary.value.selections.length > 0)
</script>

<style scoped>
.summary-expand-enter-active,
.summary-expand-leave-active {
  transition:
    max-height 0.18s ease,
    opacity 0.18s ease;
  overflow: hidden;
  max-height: 400px;
}

.summary-expand-enter-from,
.summary-expand-leave-to {
  max-height: 0;
  opacity: 0;
}
</style>
